// ECHOLOOP — intro cinematic. A fully self-contained, animated opening scene
// (NOT the gameplay world): a lone runner hits a dead-end wall, the Loop rewinds
// and prints a frozen echo of them, and the next runner climbs their own past
// self to clear the wall — the whole game premise, wordlessly, in ~16s. Then it
// transitions into the menu.
//
// Presentation only — no sim, no level. Canvas 2D, reuses the game's palette and
// font. Skippable (any key / tap); plays once per save (see main.ts seen-flag).

import { VIEW_W, VIEW_H } from './types';

const CYAN = '#56f3ff';
const MAGENTA = '#d44bff';

// ~15.5s cold-open. Re-paced from a cramped 11s so each beat has room and the
// core idea READS: RUN 1 hits the wall and can't clear it → the Loop records
// that run and PRINTS a frozen echo of it → RUN 2 climbs that past self and
// vaults the wall. Captions/title are scheduled into the GAPS between motion
// beats (never over a moving figure), and transitions are eased, not snapped.
const TOTAL = 15.5;

// Named beat boundaries (seconds) so the choreography + captions + title all
// reference ONE timeline and stay in lockstep — no magic numbers drifting apart.
const T = {
  reveal: 0.8, // void + floor + wall fade up
  run1In: 2.6, // RUN 1 strides toward the wall
  run1Leap: 4.4, // RUN 1 leaps, falls short of the top
  record: 6.2, // the Loop rewinds and prints the frozen echo (the key beat)
  run2In: 8.6, // RUN 2 strides in
  run2Climb: 9.9, // RUN 2 steps onto the echo's head
  run2Vault: 10.7, // RUN 2 vaults cleanly over the wall
  pull: 12.0, // pull-back reveals the receding column of past selves
  title: 12.3, // wordmark draws in
} as const;

interface Spark {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  size: number;
  color: string;
}

interface GhostPose {
  x: number;
  bottom: number;
  scale: number;
  hue: number;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
function easeOut(v: number): number {
  return 1 - (1 - v) * (1 - v);
}
function easeInOut(v: number): number {
  return v < 0.5 ? 2 * v * v : 1 - Math.pow(-2 * v + 2, 2) / 2;
}
/** progress 0..1 across [start, start+dur]; clamped. */
function seg(t: number, start: number, dur: number): number {
  return clamp01((t - start) / dur);
}

export class Intro {
  private t = 0;
  done = false;

  private sparks: Spark[] = [];
  private frozen: GhostPose[] = []; // echoes already printed (drawn frozen)
  private bloomedFail = false;
  private bloomedClimb = false;

  // beat layout (view space)
  private readonly groundY = VIEW_H * 0.72;
  private readonly wallX = VIEW_W * 0.6;
  private readonly wallTopY = VIEW_H * 0.72 - 150;
  private readonly figH = 46;

  /** Skip straight to the end (any key / tap). */
  skip(): void {
    this.done = true;
  }

  update(dt: number): void {
    if (this.done) return;
    this.t += Math.min(0.05, dt);
    for (let i = this.sparks.length - 1; i >= 0; i--) {
      const s = this.sparks[i]!;
      s.life += dt;
      if (s.life >= s.max) {
        this.sparks.splice(i, 1);
        continue;
      }
      s.vy += 380 * dt;
      s.x += s.vx * dt;
      s.y += s.vy * dt;
    }
    if (this.t >= TOTAL) this.done = true;
  }

  private burst(x: number, y: number, color: string, n: number, speed: number): void {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = speed * (0.3 + Math.random() * 0.7);
      this.sparks.push({
        x,
        y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp - speed * 0.3,
        life: 0,
        max: 0.5 + Math.random() * 0.6,
        size: 1.5 + Math.random() * 2.5,
        color,
      });
    }
  }

  // ---- drawing ----------------------------------------------------------------

  draw(ctx: CanvasRenderingContext2D, time: number): void {
    const t = this.t;
    ctx.clearRect(0, 0, VIEW_W, VIEW_H);

    // Backdrop: a deep void that fades up (distinct, emptier than gameplay).
    const reveal = seg(t, T.reveal, 1.4);
    this.drawVoid(ctx, time, reveal);

    // Opening point of light before the void resolves.
    const open = 1 - seg(t, T.reveal, 1.0);
    if (open > 0.01) {
      ctx.save();
      ctx.globalAlpha = open;
      const r = 4 + (1 - open) * 60;
      const g = ctx.createRadialGradient(VIEW_W / 2, VIEW_H / 2, 0, VIEW_W / 2, VIEW_H / 2, r);
      g.addColorStop(0, '#ffffff');
      g.addColorStop(0.5, CYAN);
      g.addColorStop(1, 'transparent');
      ctx.fillStyle = g;
      ctx.fillRect(VIEW_W / 2 - r, VIEW_H / 2 - r, r * 2, r * 2);
      ctx.restore();
    }

    // Pull-back zoom for the finale (everything scales down a touch).
    const pull = easeInOut(seg(t, T.pull, 1.7));
    ctx.save();
    const sc = 1 - pull * 0.14;
    ctx.translate(VIEW_W / 2, VIEW_H * 0.52);
    ctx.scale(sc, sc);
    ctx.translate(-VIEW_W / 2, -VIEW_H * 0.52);

    if (reveal > 0.02) {
      this.drawFloor(ctx, time, reveal);
      this.drawWall(ctx, time, reveal);
    }

    // Frozen echoes printed by earlier beats.
    for (const g of this.frozen) this.drawFigure(ctx, g.x, g.bottom, g.scale, `hsl(${g.hue},95%,68%)`, '#ffffff', 0.6, 1, time, true);

    // Finale: a receding column of past selves implying infinite recursion.
    if (pull > 0.01) {
      for (let i = 1; i <= 4; i++) {
        const a = pull * Math.max(0, 0.5 - i * 0.1);
        if (a <= 0) continue;
        const gx = this.wallX + 110 + i * 46;
        const gy = this.groundY - i * 10;
        this.drawFigure(ctx, gx, gy, 1 - i * 0.16, MAGENTA, '#ffffff', a, 1, time, true);
      }
    }

    // The live runner choreography.
    this.drawRunner(ctx, time);

    // Rewind sweep (the Loop resetting) during the print beat.
    this.drawRewind(ctx, t);

    // Sparks.
    for (const s of this.sparks) {
      const a = 1 - s.life / s.max;
      ctx.globalAlpha = a;
      ctx.fillStyle = s.color;
      ctx.fillRect(s.x - s.size / 2, s.y - s.size / 2, s.size, s.size);
    }
    ctx.globalAlpha = 1;

    ctx.restore();

    // Title + captions (screen space, above the pull-back).
    this.drawTitle(ctx, t, time);
    this.drawCaption(ctx, t);
    this.drawSkipHint(ctx, t, time);

    // Final fade to black handing off to the menu.
    const out = seg(t, TOTAL - 0.8, 0.8);
    if (out > 0) {
      ctx.fillStyle = `rgba(10,6,19,${out})`;
      ctx.fillRect(0, 0, VIEW_W, VIEW_H);
    }
    // Vignette.
    const vg = ctx.createRadialGradient(VIEW_W / 2, VIEW_H / 2, VIEW_H * 0.4, VIEW_W / 2, VIEW_H / 2, VIEW_H * 0.95);
    vg.addColorStop(0, 'transparent');
    vg.addColorStop(1, 'rgba(2,0,8,0.6)');
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, VIEW_W, VIEW_H);
  }

  private drawVoid(ctx: CanvasRenderingContext2D, time: number, reveal: number): void {
    const g = ctx.createLinearGradient(0, 0, 0, VIEW_H);
    g.addColorStop(0, '#0d0719');
    g.addColorStop(0.55, '#0a0613');
    g.addColorStop(1, '#06040c');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, VIEW_W, VIEW_H);
    if (reveal <= 0) return;

    // Big slow chrono-ring — spins backward briefly during the record/rewind beat.
    const spinBack = seg(this.t, T.record, 1.0);
    const cx = VIEW_W * 0.72;
    const cy = VIEW_H * 0.34;
    ctx.save();
    ctx.globalAlpha = 0.08 * reveal;
    ctx.strokeStyle = CYAN;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(cx, cy, 200, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([4, 24]);
    ctx.lineWidth = 16;
    const rot = time * 0.06 - spinBack * 6;
    ctx.beginPath();
    ctx.arc(cx, cy, 165, rot, rot + Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();

    // Drifting motes.
    ctx.save();
    for (let i = 0; i < 36; i++) {
      const seed = i * 71.3;
      const mx = ((seed * 13.7 + time * (5 + (i % 5) * 3)) % (VIEW_W + 80)) - 40;
      const my = (seed * 7.3) % VIEW_H;
      const tw = 0.5 + 0.5 * Math.sin(time * 1.5 + seed);
      ctx.globalAlpha = (0.05 + tw * 0.1) * reveal;
      ctx.fillStyle = i % 3 === 0 ? MAGENTA : '#b7a9e8';
      ctx.beginPath();
      ctx.arc(mx, my, 1 + (i % 3), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  private drawFloor(ctx: CanvasRenderingContext2D, time: number, reveal: number): void {
    ctx.save();
    ctx.globalAlpha = reveal;
    const y = this.groundY;
    const grad = ctx.createLinearGradient(0, y, 0, y + 3);
    grad.addColorStop(0, CYAN);
    grad.addColorStop(1, 'transparent');
    ctx.fillStyle = grad;
    ctx.fillRect(0, y, VIEW_W, 3);
    ctx.globalAlpha = 0.12 * reveal;
    ctx.fillStyle = CYAN;
    ctx.fillRect(0, y, VIEW_W, 26);
    // faint ground ticks scrolling
    ctx.globalAlpha = 0.18 * reveal;
    ctx.fillStyle = '#6a5e8a';
    const step = 48;
    const ox = -((time * 16) % step);
    for (let x = ox; x < VIEW_W; x += step) ctx.fillRect(x, y + 6, 14, 1.5);
    ctx.restore();
  }

  private drawWall(ctx: CanvasRenderingContext2D, time: number, reveal: number): void {
    ctx.save();
    ctx.globalAlpha = reveal;
    const x = this.wallX;
    const top = this.wallTopY;
    const h = this.groundY - top;
    ctx.fillStyle = '#161126';
    ctx.fillRect(x, top, 16, h);
    ctx.fillStyle = MAGENTA;
    ctx.globalAlpha = (0.5 + 0.2 * Math.sin(time * 3)) * reveal;
    ctx.fillRect(x, top, 16, 2.5);
    ctx.fillRect(x, top, 2.5, h);
    ctx.restore();
  }

  private drawRewind(ctx: CanvasRenderingContext2D, t: number): void {
    const k = seg(t, T.record, 0.85);
    if (k <= 0 || k >= 1) return;
    const a = Math.sin(k * Math.PI); // 0→1→0
    const x = (1 - k) * (VIEW_W + 160) - 80;
    const sg = ctx.createLinearGradient(x - 80, 0, x + 30, 0);
    sg.addColorStop(0, 'transparent');
    sg.addColorStop(0.7, `rgba(212,75,255,${0.12 * a})`);
    sg.addColorStop(0.9, `rgba(86,243,255,${0.5 * a})`);
    sg.addColorStop(1, `rgba(244,248,255,${0.75 * a})`);
    ctx.fillStyle = sg;
    ctx.fillRect(x - 80, 0, 110, VIEW_H);
  }

  /** Drives the two runners + the two echo prints across the timeline. Each
   *  beat now lands in its OWN window so a caption never sits over a figure mid-
   *  stride and the record→replay→climb logic reads in clean, separated steps. */
  private drawRunner(ctx: CanvasRenderingContext2D, time: number): void {
    const t = this.t;
    const x0 = VIEW_W * 0.16;
    const wallBase = this.wallX - 34;

    // ---- RUN 1: stride to the wall, leap, fall well short, then get RECORDED.
    // Stays visible right up to the record beat so the leap fully resolves and
    // the figure is clearly STILL at the wall when the Loop captures it.
    if (t >= T.run1In && t < T.record + 0.85) {
      const walk = easeOut(seg(t, T.run1In, 1.5)); // 1.5s walk-in (was rushed)
      const x = x0 + (wallBase - x0) * walk;
      let bottom = this.groundY;
      // a genuine leap at the wall that peaks far below its top — "can't clear it".
      // Slower hop (1.1s) so the fall-short reads, then the runner settles.
      const hop = seg(t, T.run1Leap, 1.1);
      if (hop > 0) bottom = this.groundY - Math.sin(hop * Math.PI) * 80;
      // dissolve only AS the record sweep passes over it (it becomes the echo).
      const alpha = 1 - seg(t, T.record, 0.7);
      if (alpha > 0.02) this.drawFigure(ctx, x, bottom, 1, '#f4f8ff', CYAN, alpha, 1, time, false);
      // print the first echo, frozen at the wall base, as the sweep completes.
      if (!this.bloomedFail && t >= T.record + 0.6) {
        this.bloomedFail = true;
        this.frozen.push({ x: wallBase, bottom: this.groundY, scale: 1, hue: 290 });
        this.burst(wallBase, this.groundY - this.figH / 2, MAGENTA, 28, 240);
        this.burst(wallBase, this.groundY - this.figH / 2, '#ffffff', 12, 170);
      }
    }

    // ---- RUN 2: stride in, step UP onto echo 1's head, vault over the wall.
    // Separated cleanly from RUN 1 (starts ~2s after the record beat) so the
    // viewer first SEES the frozen echo standing there, then watches it be used.
    if (t >= T.run2In && t < T.run2Vault + 1.4) {
      const walk = easeOut(seg(t, T.run2In, 1.3)); // unhurried approach
      const climb = seg(t, T.run2Climb, 0.8); // step onto the head
      const vault = seg(t, T.run2Vault, 1.1); // arc over the wall
      let x: number;
      let bottom: number;
      if (vault > 0) {
        // one clean parabola: head-height -> over the wall top -> far-side ground
        x = wallBase + (this.wallX + 90 - wallBase) * vault;
        const base = this.groundY - this.figH + this.figH * vault;
        bottom = base - Math.sin(vault * Math.PI) * 150;
      } else if (climb > 0) {
        // step up onto the echo's head (clearly grounded on the past self)
        x = wallBase;
        bottom = this.groundY - easeOut(climb) * this.figH;
      } else {
        x = x0 + (wallBase - x0) * walk;
        bottom = this.groundY;
      }
      this.drawFigure(ctx, x, bottom, 1, '#f4f8ff', CYAN, 1, 1, time, false);

      // print the second echo as it clears, behind the wall.
      if (!this.bloomedClimb && vault >= 0.98) {
        this.bloomedClimb = true;
        this.frozen.push({ x: this.wallX + 90, bottom: this.groundY, scale: 1, hue: 312 });
        this.burst(this.wallX + 90, this.groundY - this.figH / 2, MAGENTA, 22, 230);
      }
    }
  }

  private drawFigure(
    ctx: CanvasRenderingContext2D,
    x: number,
    bottom: number,
    scale: number,
    body: string,
    visor: string,
    alpha: number,
    face: 1 | -1,
    time: number,
    frozen: boolean,
  ): void {
    const w = 26 * scale;
    const h = this.figH * scale;
    const cx = x;
    ctx.save();
    ctx.globalAlpha = alpha;
    // glow
    const rg = ctx.createRadialGradient(cx, bottom - h / 2, 2, cx, bottom - h / 2, 34 * scale);
    rg.addColorStop(0, `${visor}33`);
    rg.addColorStop(1, 'transparent');
    ctx.fillStyle = rg;
    ctx.fillRect(cx - 34 * scale, bottom - h / 2 - 34 * scale, 68 * scale, 68 * scale);
    // body
    ctx.fillStyle = body;
    ctx.beginPath();
    ctx.roundRect(cx - w / 2, bottom - h, w, h, 9 * scale);
    ctx.fill();
    // visor
    ctx.fillStyle = visor;
    const vw = w * 0.42;
    const vx = face === 1 ? cx + w / 2 - vw - 3 * scale : cx - w / 2 + 3 * scale;
    ctx.beginPath();
    ctx.roundRect(vx, bottom - h + 9 * scale, vw, 6 * scale, 2);
    ctx.fill();
    if (frozen) {
      // scanline shimmer + anchor ring
      ctx.globalAlpha = alpha * 0.5;
      ctx.strokeStyle = visor;
      ctx.beginPath();
      ctx.ellipse(cx, bottom, 16 * scale, 4.5 * scale, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.globalAlpha = alpha * 0.18;
      ctx.fillStyle = '#0a0613';
      for (let yy = bottom - h; yy < bottom; yy += 3) {
        ctx.fillRect(cx - w / 2, yy + ((time * 16) % 3), w, 1);
      }
    }
    ctx.restore();
  }

  private drawTitle(ctx: CanvasRenderingContext2D, t: number, time: number): void {
    const k = seg(t, T.title, 1.2);
    if (k <= 0) return;
    const cx = VIEW_W / 2;
    const cy = VIEW_H * 0.4;

    // three-arc loop sigil (matches the favicon) drawing in
    ctx.save();
    ctx.globalAlpha = easeOut(k);
    ctx.lineCap = 'round';
    ctx.lineWidth = 4;
    const arcs = [
      { r: 26, col: 'rgba(212,75,255,0.45)' },
      { r: 33, col: 'rgba(212,75,255,0.8)' },
      { r: 40, col: CYAN },
    ];
    const sweep = easeOut(k) * Math.PI * 1.6;
    for (let i = 0; i < arcs.length; i++) {
      ctx.strokeStyle = arcs[i]!.col;
      ctx.beginPath();
      ctx.arc(cx, cy - 58, arcs[i]!.r, -Math.PI / 2 - sweep / 2 + i * 0.05, -Math.PI / 2 + sweep / 2);
      ctx.stroke();
    }
    ctx.restore();

    // wordmark
    ctx.save();
    ctx.globalAlpha = easeOut(seg(t, T.title + 0.4, 1.0));
    ctx.fillStyle = '#f4f8ff';
    ctx.textAlign = 'center';
    ctx.font = '800 58px ui-monospace, "SF Mono", Menlo, monospace';
    this.spacedText(ctx, 'ECHOLOOP', cx, cy, 9);
    ctx.restore();

    // subtitle
    ctx.save();
    ctx.globalAlpha = easeOut(seg(t, T.title + 0.9, 1.0)) * (0.7 + 0.3 * Math.sin(time * 2));
    ctx.fillStyle = MAGENTA;
    ctx.textAlign = 'center';
    ctx.font = '500 15px ui-monospace, "SF Mono", Menlo, monospace';
    this.spacedText(ctx, 'you were never alone in here', cx, cy + 30, 3);
    ctx.restore();
  }

  // Captions are scheduled into the GAPS between motion beats so a line never
  // shares the moment with a moving figure, and the last one clears well before
  // the title draws in (~T.title). Each gets a comfortable dwell to be read.
  private readonly captions: Array<{ start: number; end: number; text: string }> = [
    { start: 1.0, end: 2.6, text: 'THE LOOP — RUN 001' }, // over the empty reveal
    { start: 5.1, end: 6.0, text: 'no way over.  no way out.' }, // after the failed leap
    { start: 7.3, end: 8.4, text: 'but the loop keeps a copy of you.' }, // over the frozen echo
    { start: 9.9, end: 11.4, text: 'so stand on who you were.' }, // as RUN 2 climbs + vaults
  ];

  private drawCaption(ctx: CanvasRenderingContext2D, t: number): void {
    for (const c of this.captions) {
      if (t < c.start || t > c.end + 0.4) continue;
      const chars = Math.min(c.text.length, Math.floor((t - c.start) * 26));
      const fade = t > c.end ? 1 - seg(t, c.end, 0.4) : Math.min(1, (t - c.start) * 3);
      ctx.save();
      ctx.globalAlpha = fade;
      ctx.fillStyle = 'rgba(214,226,240,0.9)';
      ctx.textAlign = 'center';
      ctx.font = '500 16px ui-monospace, "SF Mono", Menlo, monospace';
      this.spacedText(ctx, c.text.slice(0, chars), VIEW_W / 2, VIEW_H - 64, 1);
      ctx.restore();
    }
  }

  private drawSkipHint(ctx: CanvasRenderingContext2D, t: number, time: number): void {
    if (t > TOTAL - 1) return;
    ctx.save();
    ctx.globalAlpha = 0.4 + 0.15 * Math.sin(time * 3);
    ctx.fillStyle = 'rgba(214,226,240,0.6)';
    ctx.textAlign = 'right';
    ctx.font = '600 11px ui-monospace, "SF Mono", Menlo, monospace';
    ctx.fillText('PRESS ANY KEY — SKIP', VIEW_W - 20, VIEW_H - 22);
    ctx.restore();
  }

  private spacedText(ctx: CanvasRenderingContext2D, str: string, x: number, y: number, spacing: number): void {
    if (spacing <= 0) {
      ctx.fillText(str, x, y);
      return;
    }
    const chars = str.split('');
    let total = 0;
    for (const c of chars) total += ctx.measureText(c).width + spacing;
    let cx = x - total / 2;
    const prev = ctx.textAlign;
    ctx.textAlign = 'left';
    for (const c of chars) {
      ctx.fillText(c, cx, y);
      cx += ctx.measureText(c).width + spacing;
    }
    ctx.textAlign = prev;
  }
}
